import type { AddCompInput, SessionUser, Valuation, ValuationAssumptions } from '@motoveo/shared';
import { CONDITION_GRADES, EXTERNAL_COMP_SOURCES, EXTERNAL_COMP_TYPES } from '@motoveo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { csvToArray, dollarsToCents, formatCents, formatDate, formatMiles } from '../lib/format';

const VERDICT_PILL: Record<string, string> = {
  closable: 'conf-high',
  marginal: '',
  unrealistic: 'conf-low',
  no_anchor: '',
  no_comps: '',
};
const VERDICT_LABEL: Record<string, string> = {
  closable: 'Closable',
  marginal: 'Marginal',
  unrealistic: 'Unrealistic',
  no_anchor: 'No seller number',
  no_comps: 'Not enough comps',
};

/** Editable assumptions as UI-friendly strings (pcts shown as whole %, money as $). */
interface AssumptionForm {
  mileage_adj_per_1k: string; // $
  dealer_margin_pct: string; // %
  recon: string; // $
  listing_to_sold_discount_pct: string; // %
  condition_step: string; // $
  tolerance_pct: string; // %
}

function toForm(a: ValuationAssumptions): AssumptionForm {
  return {
    mileage_adj_per_1k: String(Math.round(a.mileage_adj_per_1k_cents / 100)),
    dealer_margin_pct: String(Math.round(a.dealer_margin_pct * 1000) / 10),
    recon: String(Math.round(a.recon_cents / 100)),
    listing_to_sold_discount_pct: String(Math.round(a.listing_to_sold_discount_pct * 1000) / 10),
    condition_step: String(Math.round(a.condition_step_cents / 100)),
    tolerance_pct: String(Math.round(a.tolerance_pct * 1000) / 10),
  };
}

function fromForm(f: AssumptionForm): Partial<ValuationAssumptions> {
  const n = (s: string): number => Number.parseFloat(s.replace(/[^0-9.]/g, '')) || 0;
  return {
    mileage_adj_per_1k_cents: Math.round(n(f.mileage_adj_per_1k) * 100),
    dealer_margin_pct: n(f.dealer_margin_pct) / 100,
    recon_cents: Math.round(n(f.recon) * 100),
    listing_to_sold_discount_pct: n(f.listing_to_sold_discount_pct) / 100,
    condition_step_cents: Math.round(n(f.condition_step) * 100),
    tolerance_pct: n(f.tolerance_pct) / 100,
  };
}

const emptyComp = (v: Valuation | undefined): Record<string, string> => ({
  comp_type: 'external_auction',
  price: '',
  price_kind: '',
  mileage: '',
  condition_grade: '',
  model_designation: v?.model_designation ?? '',
  generation: v?.generation ?? '',
  model_year: v?.model_year ? String(v.model_year) : '',
  source: 'operator_manual',
  source_url: '',
  options: '',
  notes: '',
});

/**
 * M11 FOUNDER APPRAISAL WORKSHEET — the retail range, estimated dealer-acquisition band, seller-floor
 * gap, and verdict for a submission, computed transparently from operator-entered comps with editable
 * assumptions. "Use this valuation for the launch gate" wires the band into the M10 launch gate.
 */
export function AppraisalWorksheetPage({ user }: { user: SessionUser }) {
  const { id } = useParams<{ id: string }>();
  const submissionId = id!;
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['valuation', submissionId], queryFn: () => api.valuation.get(submissionId) });
  const v = q.data?.valuation;

  const [form, setForm] = useState<AssumptionForm | null>(null);
  useEffect(() => {
    if (v && form === null) setForm(toForm(v.assumptions));
  }, [v, form]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['valuation', submissionId] });
    void qc.invalidateQueries({ queryKey: ['submission', submissionId] });
  };

  const save = useMutation({
    mutationFn: (body: { assumptions?: Partial<ValuationAssumptions>; use_for_gate?: boolean }) => api.valuation.save(submissionId, body),
    onSuccess: (res) => {
      setForm(toForm(res.valuation.assumptions));
      invalidate();
    },
  });
  const addComp = useMutation({
    mutationFn: (body: AddCompInput) => api.valuation.addComp(body),
    onSuccess: invalidate,
  });
  const delComp = useMutation({
    mutationFn: (compId: string) => api.valuation.deleteComp(compId),
    onSuccess: invalidate,
  });
  // M15 — the founder correction for a POISONED oracle row (moat bid/deal comps deleteComp refuses):
  // an audited void with a required reason; the row survives (append-only) but stops feeding the
  // oracle/gate/valuation, so it disappears from this worksheet on refetch.
  const voidComp = useMutation({
    mutationFn: ({ compId, reason }: { compId: string; reason: string }) => api.valuation.voidComp(compId, reason),
    onSuccess: invalidate,
  });
  const promptVoid = (compId: string) => {
    const reason = window.prompt('Void this comp? It stops feeding the oracle/gate/valuation (audited, not deleted).\n\nReason (required):');
    if (reason && reason.trim().length >= 3) voidComp.mutate({ compId, reason: reason.trim() });
  };

  const [comp, setComp] = useState<Record<string, string>>(emptyComp(undefined));
  useEffect(() => {
    // seed the add-comp form's model/generation from the subject once loaded.
    if (v && !comp.model_designation && v.model_designation) setComp((c) => ({ ...c, model_designation: v.model_designation ?? '', generation: v.generation ?? '', model_year: v.model_year ? String(v.model_year) : '' }));
  }, [v, comp.model_designation]);

  const submitComp = () => {
    const price = dollarsToCents(comp.price);
    if (!price || !comp.model_designation || !comp.generation) return;
    const body: AddCompInput = {
      comp_type: comp.comp_type as (typeof EXTERNAL_COMP_TYPES)[number],
      price_cents: price,
      source: comp.source as (typeof EXTERNAL_COMP_SOURCES)[number],
      model_designation: comp.model_designation,
      generation: comp.generation,
      ...(comp.price_kind ? { price_kind: comp.price_kind as 'asking' | 'sold' | 'realized_retail' } : {}),
      ...(comp.mileage ? { mileage: Number.parseInt(comp.mileage, 10) } : {}),
      ...(comp.condition_grade ? { condition_grade: comp.condition_grade as (typeof CONDITION_GRADES)[number] } : {}),
      ...(comp.model_year ? { model_year: Number.parseInt(comp.model_year, 10) } : {}),
      ...(comp.source_url ? { source_url: comp.source_url } : {}),
      ...(comp.options ? { high_value_option_codes: csvToArray(comp.options) } : {}),
      ...(comp.notes ? { notes: comp.notes } : {}),
    };
    addComp.mutate(body, { onSuccess: () => setComp(emptyComp(v)) });
  };

  const gapClass = useMemo(() => (v && v.gap_cents != null && v.gap_cents > 0 ? 'error' : ''), [v]);

  return (
    <Layout user={user}>
      <p>
        <Link to={`/submissions/${submissionId}`} className="muted">
          ← Submission
        </Link>
      </p>
      <h2>Appraisal worksheet</h2>
      <p className="muted">
        Is the seller’s number within a plausible dealer-bid range? Enter comps you gathered (BaT / Cars &amp; Bids / etc — no scraping),
        tune the transparent assumptions, and — when it looks right — wire the dealer-acquisition band into the launch gate.
      </p>

      {q.isLoading ? (
        <p className="muted">Computing…</p>
      ) : q.isError ? (
        <p className="error">{(q.error as Error).message}</p>
      ) : !v ? null : !v.model_designation ? (
        <div className="card">
          <p className="muted">This submission isn’t decoded to a spec yet — decode the VIN first so comps can be matched to it.</p>
        </div>
      ) : (
        <>
          {/* ---- the verdict + bands ---- */}
          <div className="card">
            <div className="row-between">
              <h3>
                {v.model_designation} {v.model_year ?? ''} <span className="muted">· {v.generation}</span>
              </h3>
              <span className={`pill ${VERDICT_PILL[v.verdict] ?? ''}`} style={{ fontSize: '1rem' }}>
                {VERDICT_LABEL[v.verdict] ?? v.verdict} · confidence {v.confidence}
              </span>
            </div>
            <div className="form-grid">
              <div className="field">
                <span className="field-label">Retail range (adjusted comps)</span>
                <span className="field-value">
                  {formatCents(v.retail.low_cents)} – {formatCents(v.retail.high_cents)}
                  <span className="muted"> (mid {formatCents(v.retail.mid_cents)})</span>
                </span>
              </div>
              <div className="field">
                <span className="field-label">Est. dealer-acquisition band</span>
                <span className="field-value">
                  {formatCents(v.dealer_acq.low_cents)} – {formatCents(v.dealer_acq.high_cents)}
                  {v.dealer_acq_source ? <span className="muted"> ({v.dealer_acq_source === 'retail_margin' ? 'retail − margin' : 'dealer comps'})</span> : null}
                </span>
              </div>
              <div className="field">
                <span className="field-label">Seller {v.anchor_source === 'seller_floor' ? 'floor' : 'asking'} vs band</span>
                <span className={`field-value ${gapClass}`}>
                  {formatCents(v.seller_anchor_cents)}
                  {v.gap_cents != null ? (
                    <span className="muted">
                      {' '}
                      ({v.gap_cents > 0 ? '+' : ''}
                      {formatCents(v.gap_cents)} vs high{v.gap_pct != null ? `, ${Math.round(v.gap_pct * 100)}%` : ''})
                    </span>
                  ) : null}
                </span>
              </div>
            </div>
            <ul className="plain muted" style={{ marginTop: '0.5rem' }}>
              {v.reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
            <div className="row-between">
              <span className="muted">
                {v.persisted?.use_for_gate ? (
                  <span className="pill conf-high">Wired into the launch gate</span>
                ) : (
                  <span className="pill">Not wired to the gate</span>
                )}
                {v.persisted ? <span className="muted"> · saved {formatDate(v.persisted.updated_at)}</span> : ' · not saved yet'}
              </span>
              <button
                type="button"
                disabled={save.isPending || v.verdict === 'no_comps'}
                onClick={() => save.mutate({ assumptions: form ? fromForm(form) : undefined, use_for_gate: true })}
              >
                {save.isPending ? 'Saving…' : 'Use this valuation for the launch gate →'}
              </button>
            </div>
            {save.isError ? <p className="error">{(save.error as Error).message}</p> : null}
          </div>

          {/* ---- editable assumptions (transparent, no black box) ---- */}
          {form ? (
            <div className="card form">
              <h3>Assumptions (editable — no black box)</h3>
              <div className="form-grid">
                <label>
                  Mileage $ / 1,000 mi
                  <input value={form.mileage_adj_per_1k} onChange={(e) => setForm({ ...form, mileage_adj_per_1k: e.target.value })} />
                </label>
                <label>
                  Dealer margin %
                  <input value={form.dealer_margin_pct} onChange={(e) => setForm({ ...form, dealer_margin_pct: e.target.value })} />
                </label>
                <label>
                  Recon $ (flat)
                  <input value={form.recon} onChange={(e) => setForm({ ...form, recon: e.target.value })} />
                </label>
                <label>
                  Listing → sold discount %
                  <input value={form.listing_to_sold_discount_pct} onChange={(e) => setForm({ ...form, listing_to_sold_discount_pct: e.target.value })} />
                </label>
                <label>
                  Condition $ / grade
                  <input value={form.condition_step} onChange={(e) => setForm({ ...form, condition_step: e.target.value })} />
                </label>
                <label>
                  Tolerance % (marginal band)
                  <input value={form.tolerance_pct} onChange={(e) => setForm({ ...form, tolerance_pct: e.target.value })} />
                </label>
              </div>
              <div className="row-between">
                <span className="muted">Option deltas are set per code via the API/CSV. Saving recomputes the band from these values.</span>
                <button type="button" disabled={save.isPending} onClick={() => save.mutate({ assumptions: fromForm(form) })}>
                  {save.isPending ? 'Recomputing…' : 'Recompute & save'}
                </button>
              </div>
            </div>
          ) : null}

          {/* ---- comps table (filtered to this spec) ---- */}
          <div className="card">
            <div className="row-between">
              <h3>Comps for this spec ({v.comp_count})</h3>
              <span className="muted">
                {v.retail_comp_count} retail · {v.dealer_comp_count} dealer
              </span>
            </div>
            {v.comps.length === 0 ? (
              <p className="muted">No comps yet — add the ones you gathered below. They match loosely on model + generation.</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Price</th>
                      <th>Adjusted</th>
                      <th>Miles</th>
                      <th>Cond.</th>
                      <th>Source</th>
                      <th>Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.comps.map((c) => (
                      <tr key={c.id}>
                        <td className="muted">
                          {c.comp_type.replace('external_', '')}
                          {c.is_sample ? <span className="pill"> sample</span> : null}
                        </td>
                        <td>
                          {c.source_url ? (
                            <a href={c.source_url} target="_blank" rel="noreferrer">
                              {formatCents(c.price_cents)}
                            </a>
                          ) : (
                            formatCents(c.price_cents)
                          )}
                        </td>
                        <td>
                          <strong>{formatCents(c.adjusted_value_cents)}</strong>
                        </td>
                        <td className="muted">{formatMiles(c.mileage)}</td>
                        <td className="muted">{c.condition_grade ?? '—'}</td>
                        <td className="muted">{c.source}</td>
                        <td className="muted">{formatDate(c.observed_at)}</td>
                        <td>
                          {c.editable ? (
                            <button type="button" className="plain" disabled={delComp.isPending} onClick={() => delComp.mutate(c.id)}>
                              delete
                            </button>
                          ) : (
                            <>
                              <span className="muted">moat</span>{' '}
                              <button
                                type="button"
                                className="plain"
                                title="Audit-void a poisoned comp (reason required; the row is kept but stops feeding the oracle)"
                                disabled={voidComp.isPending}
                                onClick={() => promptVoid(c.id)}
                              >
                                void
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {voidComp.isError ? <p className="error">{(voidComp.error as Error).message}</p> : null}
                {delComp.isError ? <p className="error">{(delComp.error as Error).message}</p> : null}
              </div>
            )}
          </div>

          {/* ---- add a comp by hand ---- */}
          <div className="card form">
            <h3>Add a comp</h3>
            <div className="form-grid">
              <label>
                Type
                <select value={comp.comp_type} onChange={(e) => setComp({ ...comp, comp_type: e.target.value })}>
                  {EXTERNAL_COMP_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t.replace('external_', '')}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Price ($)
                <input value={comp.price} onChange={(e) => setComp({ ...comp, price: e.target.value })} placeholder="215,000" />
              </label>
              <label>
                Sold / listed
                <select value={comp.price_kind} onChange={(e) => setComp({ ...comp, price_kind: e.target.value })}>
                  <option value="">(from type)</option>
                  <option value="sold">sold</option>
                  <option value="asking">asking (listed)</option>
                </select>
              </label>
              <label>
                Mileage
                <input value={comp.mileage} onChange={(e) => setComp({ ...comp, mileage: e.target.value })} placeholder="4200" />
              </label>
              <label>
                Condition
                <select value={comp.condition_grade} onChange={(e) => setComp({ ...comp, condition_grade: e.target.value })}>
                  <option value="">—</option>
                  {CONDITION_GRADES.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Source
                <select value={comp.source} onChange={(e) => setComp({ ...comp, source: e.target.value })}>
                  {EXTERNAL_COMP_SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Model designation
                <input value={comp.model_designation} onChange={(e) => setComp({ ...comp, model_designation: e.target.value })} />
              </label>
              <label>
                Generation
                <input value={comp.generation} onChange={(e) => setComp({ ...comp, generation: e.target.value })} />
              </label>
              <label>
                Model year
                <input value={comp.model_year} onChange={(e) => setComp({ ...comp, model_year: e.target.value })} />
              </label>
              <label>
                Source URL (dedupes)
                <input value={comp.source_url} onChange={(e) => setComp({ ...comp, source_url: e.target.value })} placeholder="https://bringatrailer.com/listing/…" />
              </label>
              <label>
                High-value option codes (csv)
                <input value={comp.options} onChange={(e) => setComp({ ...comp, options: e.target.value })} placeholder="450, 640" />
              </label>
              <label>
                Notes
                <input value={comp.notes} onChange={(e) => setComp({ ...comp, notes: e.target.value })} />
              </label>
            </div>
            <div className="row-between">
              {addComp.isError ? <span className="error">{(addComp.error as Error).message}</span> : <span className="muted">Re-adding the same source URL updates that comp instead of duplicating it.</span>}
              <button type="button" disabled={addComp.isPending || !comp.price || !comp.model_designation} onClick={submitComp}>
                {addComp.isPending ? 'Adding…' : 'Add comp'}
              </button>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
